// Tarifs fournisseurs dans l'interface : comparaison entre fournisseurs, historique daté, provenance d'un prix,
// revue des prix d'un devis (mise à jour EXPLICITE au tarif du JJ/MM/AAAA, jamais silencieuse).
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ArrowRightLeft, History, RefreshCw } from 'lucide-react';
import { api, messageErreur } from '../../lib/api.ts';
import { date, dateHeure, euros, pluriel } from '../../lib/format.ts';
import { Alerte, Badge, Bouton, Chargement, Dialogue, EtatErreur, EtatVide } from '../../components/ui.tsx';
import { BadgesAlertePrix, libelleMiseAJour, Montant, sourcePrix, type Ligne, type VueRevision } from './commun.tsx';

export interface Provenance { type: string; fichier: string | null; ligne: number | null; par: string | null; le: string | null;
  corrige_par: string | null; corrige_le: string | null }
export function sourceConditionnement(s: Record<string, unknown>) {
  return [s.type === 'import' ? `Fichier « ${s.fichier} », ligne ${s.ligne}` : 'Saisie',
    s.par ? `par ${s.par}` : 'Auteur inconnu', typeof s.le === 'string' ? `le ${dateHeure(s.le)}` : null].filter(Boolean).join(' — ');
}
export interface PrixCatalogue {
  id: string; manufacturer: string | null; reference: string; label: string; unit: string; purchase_price?: string | null;
  list_price?: string | null; discount_pct?: string | null; source: string; source_label: string | null; source_libelle: string;
  price_date: string | null; supplier_id: string | null; supplier_name: string | null; ancien: boolean; provenance: Provenance;
  conditionnement: { quantite: string; libelle: string; source: { type: string; par?: string; le?: string; fichier?: string; ligne?: number } } | null;
  exacte?: boolean; nb_fournisseurs?: number; nb_versions?: number; moins_cher?: boolean;
  /** Surplus (22/10/2026) : famille de l'article (null = à décider) et taux propre. */
  surplus?: { famille: string | null; taux: string | null; source: { par?: string; le?: string; motif?: string | null } | null };
}

const pctTxt = (v: string) => `${Number(v).toLocaleString('fr-FR', { maximumFractionDigits: 2 })} %`;

/** « Fichier tarif.xlsx, ligne 12 — importé par X le … » ou « Saisi par X le … ». */
export function texteProvenance(p: Provenance) {
  const qui = p.par ? ` par ${p.par}` : '';
  const quand = p.le ? ` le ${dateHeure(p.le)}` : '';
  const base = p.type === 'import' && p.fichier
    ? `Fichier « ${p.fichier} »${p.ligne ? `, ligne ${p.ligne}` : ''} — importé${qui}${quand}`
    : `${p.type === 'catalogue' ? 'Catalogue autorisé, saisi' : 'Saisi'}${qui}${quand}`;
  return p.corrige_par && p.corrige_le ? `${base} ; corrigé par ${p.corrige_par} le ${dateHeure(p.corrige_le)}` : base;
}

/** Prix net, et prix brut − remise quand le tarif les donne. */
export function DetailPrix({ p }: { p: PrixCatalogue }) {
  return (
    <span className="devis-detail-prix">
      <Montant v={p.purchase_price ?? null} /> <span className="tres-petit discret">net HT / {p.unit}</span>
      {p.list_price && p.discount_pct && <span className="tres-petit discret"> — brut {euros(p.list_price)}, remise {pctTxt(p.discount_pct)}</span>}
    </span>
  );
}

/** Comparaison d'une référence entre fournisseurs (dernier prix de chacun) et historique complet, en ligne (sans dialogue imbriqué). */
export function ListeComparaison({ reference, action, actionLibelle, actuelId }: {
  reference: string; action?: (p: PrixCatalogue) => Promise<unknown>; actionLibelle?: string; actuelId?: string | null;
}) {
  const q = useQuery({ queryKey: ['prix-reference', reference.toLowerCase()],
    queryFn: () => api.get<{ fournisseurs: PrixCatalogue[]; historique: PrixCatalogue[] }>(`/api/prix/par-reference?reference=${encodeURIComponent(reference)}`) });
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState<string | null>(null);
  if (q.isLoading) return <Chargement lignes={2} />;
  if (q.isError) return <EtatErreur erreur={q.error} reessayer={() => q.refetch()} />;
  if (!q.data || !q.data.fournisseurs.length) return <EtatVide titre="Aucun prix pour cette référence" />;
  return (
    <div className="pile devis-comparaison">
      {erreur && <Alerte ton="danger">{erreur}</Alerte>}
      <ul className="devis-resultats" aria-label={`Prix de la référence ${reference} par fournisseur`}>
        {q.data.fournisseurs.map((p) => (
          <li key={p.id} className="devis-resultat">
            <div className="pile devis-resultat-texte">
              <span className="rangee" style={{ gap: 6 }}>
                <strong>{p.supplier_name ?? p.source_libelle}</strong>
                {p.moins_cher && <Badge ton="succes">Moins cher</Badge>}
                {p.ancien && <Badge ton="ambre">Prix ancien</Badge>}
                {actuelId === p.id && <Badge ton="info">Prix de la ligne</Badge>}
              </span>
              <DetailPrix p={p} />
              <span className="tres-petit discret">{`Tarif du ${p.price_date ? date(p.price_date) : 'date inconnue'} — ${texteProvenance(p.provenance)}`}</span>
            </div>
            {action && actuelId !== p.id && p.purchase_price !== null && (
              <Bouton compact chargement={envoi === p.id} onClick={async () => {
                setErreur(null); setEnvoi(p.id);
                try { await action(p); } catch (x) { setErreur(messageErreur(x)); } finally { setEnvoi(null); }
              }}>{actionLibelle ?? 'Utiliser ce prix'}</Bouton>
            )}
          </li>
        ))}
      </ul>
      {q.data.historique.length > q.data.fournisseurs.length && (
        <details className="devis-historique">
          <summary>{`Historique daté (${pluriel(q.data.historique.length, 'version')})`}</summary>
          <ul className="devis-liste-simple">
            {q.data.historique.map((p) => (
              <li key={p.id} className="petit">
                <span className="chiffre">{p.price_date ? date(p.price_date) : 'Date inconnue'}</span> — {p.supplier_name ?? p.source_libelle} — <Montant v={p.purchase_price ?? null} />
                <span className="tres-petit discret"> ({texteProvenance(p.provenance)})</span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

/** Catalogue : comparaison et historique d'une référence. */
export function DialogueComparaison({ reference, fermer }: { reference: string | null; fermer: () => void }) {
  return (
    <Dialogue titre={reference ? `Référence ${reference}` : ''} ouvert={!!reference} fermer={fermer} large pied={<Bouton onClick={fermer}>Fermer</Bouton>}>
      {reference && (
        <div className="pile">
          <p className="petit secondaire">Dernier prix de chaque fournisseur, du moins cher au plus cher ; l’historique garde toutes les versions datées.</p>
          <ListeComparaison reference={reference} />
        </div>
      )}
    </Dialogue>
  );
}

// ─── Revue des prix d'un devis ───
export function nbAlertes(v: VueRevision) {
  const a = Object.values(v.alertes_prix?.lignes ?? {});
  return { anciens: a.filter((x) => x.ancien).length, propositions: a.filter((x) => x.proposition).length };
}

export function DialogueRevuePrix({ ouvert, fermer, v, modifiable, actualiser }: {
  ouvert: boolean; fermer: () => void; v: VueRevision; modifiable: boolean;
  actualiser: (lignes: { ligneId: string; priceItemId: string }[]) => Promise<unknown>;
}) {
  const alertes = v.alertes_prix;
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState<string | null>(null);
  const [compare, setCompare] = useState<string | null>(null);
  const [confirmeTout, setConfirmeTout] = useState(false);
  useEffect(() => { if (ouvert) { setErreur(null); setCompare(null); setConfirmeTout(false); } }, [ouvert]);
  if (!alertes) return null;
  const lignes = v.lignes.filter((l) => { const a = alertes.lignes[l.id]; return a && (a.ancien || a.proposition); });
  const aMettre = lignes.filter((l) => alertes.lignes[l.id].proposition);
  const faire = async (cle: string, corps: { ligneId: string; priceItemId: string }[]) => {
    setErreur(null); setEnvoi(cle);
    try { await actualiser(corps); } catch (x) { setErreur(messageErreur(x)); } finally { setEnvoi(null); }
  };
  return (
    <Dialogue titre="Revoir les prix du devis" ouvert={ouvert} fermer={fermer} large
      pied={<>
        <Bouton onClick={fermer}>Fermer</Bouton>
        {modifiable && aMettre.length > 1 && !confirmeTout && <Bouton variante="principal" icone={<RefreshCw aria-hidden />} onClick={() => setConfirmeTout(true)}>
          {`Tout mettre à jour (${aMettre.length})`}</Bouton>}
        {modifiable && confirmeTout && <Bouton variante="principal" chargement={envoi === 'tout'}
          onClick={() => faire('tout', aMettre.map((l) => ({ ligneId: l.id, priceItemId: alertes.lignes[l.id].proposition!.price_item_id })))}>
          {`Confirmer la mise à jour de ${pluriel(aMettre.length, 'ligne')}`}</Bouton>}
      </>}>
      <div className="pile">
        <p className="petit secondaire">{`Prix d’achat datés de plus de ${alertes.seuil_mois} mois, ou pour lesquels un tarif plus récent du même fournisseur existe.
          Rien n’est modifié sans votre clic ; chaque mise à jour est tracée (ancien et nouveau prix, dates).`}</p>
        {confirmeTout && (
          <Alerte ton="ambre" titre="Vérifiez avant de confirmer">
            <ul className="devis-liste-simple">
              {aMettre.map((l) => { const p = alertes.lignes[l.id].proposition!; return (
                <li key={l.id}><strong>{l.label}</strong> : {euros(l.unit_cost ?? null) ?? 'à renseigner'} ({l.cost_date ? date(l.cost_date) : '—'}) → {euros(p.prix)} ({p.date ? date(p.date) : '—'})</li>
              ); })}
            </ul>
          </Alerte>
        )}
        {erreur && <Alerte ton="danger">{erreur}</Alerte>}
        {!lignes.length && <EtatVide titre="Aucun prix à revoir" texte="Tous les prix d’achat de cette révision sont récents et à jour." />}
        {!!lignes.length && (
          <ul className="devis-resultats">
            {lignes.map((l) => <LigneRevue key={l.id} l={l} v={v} modifiable={modifiable} envoi={envoi} compare={compare === l.id}
              basculer={() => setCompare(compare === l.id ? null : l.id)}
              faire={(priceItemId) => faire(l.id, [{ ligneId: l.id, priceItemId }])} />)}
          </ul>
        )}
      </div>
    </Dialogue>
  );
}

function LigneRevue({ l, v, modifiable, envoi, compare, basculer, faire }: { l: Ligne; v: VueRevision; modifiable: boolean; envoi: string | null;
  compare: boolean; basculer: () => void; faire: (priceItemId: string) => Promise<unknown> }) {
  const a = v.alertes_prix!.lignes[l.id];
  return (
    <li className="pile" style={{ gap: 6 }}>
      <div className="pile" style={{ gap: 2 }}>
        <strong>{l.label}</strong>
        <span className="petit">{l.reference && <span className="mono">{l.reference} — </span>}{`Coût actuel ${euros(l.unit_cost ?? null) ?? 'à renseigner'}`}
          <span className="tres-petit discret"> — {sourcePrix(l)}</span></span>
        <BadgesAlertePrix a={a} seuil={v.alertes_prix!.seuil_mois} />
      </div>
      <div className="groupe-btn">
        {modifiable && a.proposition && (
          <Bouton compact variante="principal" icone={<RefreshCw aria-hidden />} chargement={envoi === l.id} onClick={() => faire(a.proposition!.price_item_id)}>
            {`${libelleMiseAJour(a.proposition)} — ${euros(a.proposition.prix)}`}</Bouton>
        )}
        {l.reference && a.nb_fournisseurs > 1 && (
          <Bouton compact icone={<ArrowRightLeft aria-hidden />} onClick={basculer} aria-expanded={compare}>
            {compare ? 'Masquer la comparaison' : `Comparer ${a.nb_fournisseurs} fournisseurs`}</Bouton>
        )}
        {l.reference && a.nb_fournisseurs <= 1 && !a.proposition && (
          <Bouton compact icone={<History aria-hidden />} onClick={basculer} aria-expanded={compare}>{compare ? 'Masquer l’historique' : 'Historique du prix'}</Bouton>
        )}
      </div>
      {compare && l.reference && <ListeComparaison reference={l.reference} actuelId={l.price_item_id ?? null}
        action={modifiable ? (p) => faire(p.id) : undefined} actionLibelle="Utiliser ce prix" />}
    </li>
  );
}
